import AddToPhotosIcon from '@mui/icons-material/AddToPhotos';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import DeleteSweepTwoToneIcon from '@mui/icons-material/DeleteSweepTwoTone';
import {
  Avatar,
  Box,
  Chip,
  Grid,
  MenuItem,
  Stack,
  SwipeableDrawer,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import { USER_ROLES, UserType } from '../../all-types';
import { CommonBtn } from '../../components';
import SearchInput from '../../components/Input/SearchInput/SearchInput';
import { useAppDispatch, useAppSelector } from '../../redux-store/hooks';
import { createUser, deleteUser, getUserList, updateUser } from '../../services';

const UserManagement: React.FC = () => {
  const theme = useTheme();
  const dispatcher = useAppDispatch();
  const users = useAppSelector((state) => state.preloadData.users || []);
  const STANDARD_HEADER_HEIGHT = useAppSelector((state) => state.ui.STANDARD_HEADER_HEIGHT);

  const [searchTxt, setSearchTxt] = useState('');
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<UserType>({
    userId: "",
    name: "",
    password: "",
    pin: 0,
    role: USER_ROLES.WAITER
  });

  useEffect(() => {
    dispatcher(getUserList());
  }, [dispatcher]);

  const handleOpenDrawer = (user?: any) => {
    if (user) {
      setFormData({ ...user, password: "" });
      setEditingId(user._id);
    } else {
      setFormData({ userId: "", name: "", password: "", pin: 0, role: USER_ROLES.WAITER });
      setEditingId(null);
    }
    setOpenDrawer(true);
  };

  const handleSave = () => {
    const action = editingId 
      ? updateUser({ id: editingId, user: formData }) 
      : createUser({ user: formData });
    
    dispatcher(action).then(() => setOpenDrawer(false));
  };

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(searchTxt.toLowerCase()) || 
    u.role.toLowerCase().includes(searchTxt.toLowerCase())
  );

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'User',
      flex: 1.5,
      renderCell: (params) => (
        <Stack direction="row" spacing={2} alignItems="center" >
          <Avatar sx={{ 
            bgcolor: theme.palette.primary.main + '20', 
            color: theme.palette.primary.main,
            fontWeight: 'bold',
            fontSize: '0.8rem',
            border: `1px solid ${theme.palette.primary.main}40`
          }}>
            {params.value.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600}>{params.value}/ ID: {params.row.userId}</Typography>
            {/* <Typography variant="caption" color="text.secondary">ID: {params.row.userId}</Typography> */}
          </Box>
        </Stack>
      ),
    },
    {
      field: 'role',
      headerName: 'Permission Role',
      flex: 1,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          variant="outlined"
          sx={{ 
            borderRadius: '6px', 
            fontWeight: 600, 
            textTransform: 'uppercase',
            fontSize: '0.65rem',
            borderColor: theme.palette.divider 
          }} 
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      flex: 0.5,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditTwoToneIcon sx={{ color: theme.palette.primary.main }} />}
          label="Edit"
          onClick={() => handleOpenDrawer(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteSweepTwoToneIcon sx={{ color: theme.palette.error.main }} />}
          label="Delete"
          onClick={() => dispatcher(deleteUser(params.id as string))}
        />,
      ],
    },
  ];

  const PANEL_HEIGHT = `calc(100vh - ${STANDARD_HEADER_HEIGHT}px)`;

  return (
    <Box sx={{ height: PANEL_HEIGHT, display: 'flex', flexDirection: 'column' }}>
      {/* Premium Header */}
      <Grid container sx={{ p: 2, alignItems: 'center', justifyContent: 'space-between' }}>
        <Grid item xs={12} md={4}>
          <SearchInput
            value={searchTxt}
            onChange={(txt) => setSearchTxt(txt)}
            placeholder="Search users or roles..."
          />
        </Grid>
        <Grid item display="flex" alignItems="center" gap={3}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Total Staff: {users.length}
            </Typography>
            <CommonBtn
                variant="contained"
                startIcon={<AddToPhotosIcon />}
                onClick={() => handleOpenDrawer()}
            >
                Add Staff Member
            </CommonBtn>
        </Grid>
      </Grid>

      {/* Data Grid Section */}
      <Box sx={{ flex: 1, px: 2, pb: 2 }}>
        <DataGrid
          rows={filteredUsers}
          columns={columns}
          getRowId={(row) => row._id}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-columnHeaders': {
                backgroundColor: theme.palette.background.paper,
                borderRadius: '8px 8px 0 0'
            }
          }}
        />
      </Box>

      {/* Side Management Drawer */}
      <SwipeableDrawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        onOpen={() => setOpenDrawer(true)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 450,
            backgroundColor: theme.palette.background.default,
            top: STANDARD_HEADER_HEIGHT,
            height: `calc(100% - ${STANDARD_HEADER_HEIGHT}px)`,
            boxShadow: '-10px 0px 30px rgba(0,0,0,0.1)',
            p: 4
          },
        }}
      >
        <Typography variant="h5" fontWeight={800} mb={1}>
          {editingId ? "Update Staff" : "New Staff Member"}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={4}>
          Configure user permissions and access credentials.
        </Typography>

        <Stack spacing={3}>
          <TextField
            fullWidth
            label="Full Name"
            variant="standard"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <TextField
            fullWidth
            label="User ID / Email"
            variant="standard"
            value={formData.userId}
            onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
          />
          <TextField
            fullWidth
            label="Access Pin/Password"
            type="password"
            variant="standard"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          />
          <TextField
            select
            fullWidth
            label="System Role"
            variant="standard"
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value as USER_ROLES })}
          >
            {Object.values(USER_ROLES).map((role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ pt: 4 }}>
            <CommonBtn 
                fullWidth 
                variant="contained" 
                size="large" 
                onClick={handleSave}
                sx={{ py: 1.5, borderRadius: '12px' }}
            >
              {editingId ? "Apply Changes" : "Create Account"}
            </CommonBtn>
            <CommonBtn 
                fullWidth 
                variant="text" 
                onClick={() => setOpenDrawer(false)} 
                sx={{ mt: 1, color: theme.palette.text.secondary }}
            >
                Cancel
            </CommonBtn>
          </Box>
        </Stack>
      </SwipeableDrawer>
    </Box>
  );
};

export { UserManagement };